import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

const MB_2 = 2 * 1024 * 1024;

/**
 * "Useful For" rules:
 * - Required (not only spaces)
 * - Min length 2, Max length 200
 * - Trim leading/trailing spaces
 * - Allowed chars: alphabets, numbers, spaces, comma (,), hyphen (-), ampersand (&)
 */
const usefulForAllowedCharsRe = /^[A-Za-z0-9 ,&-]+$/;

export const usefulForRequiredNoAllSpacesValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined) return { required: true };
    const str = String(raw);
    if (str.trim().length === 0) return { required: true };
    return null;
  };
};

export const usefulForTrimValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined) return null;
    const str = String(raw);
    if (str.length === 0) return null;
    if (str.trim() !== str) return { usefulForTrim: true };
    return null;
  };
};

export const usefulForAllowedCharsValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined) return null;
    const str = String(raw);
    if (str.length === 0) return null;
    if (!usefulForAllowedCharsRe.test(str)) return { usefulForAllowedChars: true };
    return null;
  };
};

export const usefulForMinLengthValidator = (min: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined) return null;
    const str = String(raw);
    if (str.length === 0) return null;

    const trimmed = str.trim();
    if (trimmed.length > 0 && trimmed.length < min) {
      return { usefulForMinLength: { requiredLength: min, actualLength: trimmed.length } };
    }
    return null;
  };
};

export const usefulForMaxLengthValidator = (max: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined) return null;
    const str = String(raw);
    if (str.length === 0) return null;

    const trimmed = str.trim();
    if (trimmed.length > max) {
      return { usefulForMaxLength: { requiredLength: max, actualLength: trimmed.length } };
    }
    return null;
  };
};

/**
 * Combines all "Useful For" requirements into a single validator.
 * (Kept separate as well for more granular UI if you want.)
 */
export const usefulForValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const errors: ValidationErrors = {};
    
    // First: required/not only spaces
    const requiredErr = usefulForRequiredNoAllSpacesValidator()(control);
    if (requiredErr) errors['required'] = true;

    const raw = control.value;
    const str = String(raw);
    const trimmed = str.trim();

    // Only check other validations if the field is not empty
    if (trimmed.length > 0) {
      // Trim rule
      if (str !== trimmed) errors['usefulForTrim'] = true;

      // Allowed chars
      if (!usefulForAllowedCharsRe.test(str)) errors['usefulForAllowedChars'] = true;

      // Length rule
      if (trimmed.length < 2) {
        errors['usefulForMinLength'] = { requiredLength: 2, actualLength: trimmed.length };
      }
      if (trimmed.length > 200) {
        errors['usefulForMaxLength'] = { requiredLength: 200, actualLength: trimmed.length };
      }
    }

    // Return all errors if any exist
    return Object.keys(errors).length > 0 ? errors : null;
  };
};

/**
 * Thumbnail rules:
 * - JPG/JPEG/PNG only
 * - File size <= 2MB
 * - Recommended resolution check
 * - Corrupted file detection
 */
export const thumbnailFileValidator = (options: {
  recommendedWidth: number;
  recommendedHeight: number;
  label: string;
}): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const file = control.value as File | null;
    if (!file) return null; // required is handled separately

    // Check file type
    const mime = file.type?.toLowerCase?.() ?? '';
    const allowedMimes = ['image/jpeg', 'image/jpg', 'image/png'];
    const isAllowedType = allowedMimes.includes(mime) || mime === 'image/jfif';

    // Also fall back to extension if mime is missing
    const name = (file.name || '').toLowerCase();
    const hasAllowedExt = name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png');

    if (!isAllowedType && !hasAllowedExt) {
      return { thumbnailType: true };
    }

    // Check file size
    if (file.size > MB_2) {
      return { thumbnailSize: true };
    }

    // Check if file is corrupted and validate dimensions
    return new Promise<ValidationErrors | null>((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        
        // Check if image loaded with valid dimensions
        if (img.width === 0 || img.height === 0) {
          resolve({ thumbnailCorrupted: true });
          return;
        }

        // Check recommended resolution (warning, not error)
        // We only return error for corrupted files, resolution is just a recommendation
        resolve(null);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ thumbnailCorrupted: true });
      };

      // Timeout after 5 seconds to handle potential hanging
      setTimeout(() => {
        URL.revokeObjectURL(url);
        resolve({ thumbnailCorrupted: true });
      }, 5000);

      img.src = url;
    }) as any; // TypeScript doesn't handle Promise in ValidatorFn well, but Angular does
  };
};

// Synchronous wrapper that returns null initially and validates asynchronously
export const thumbnailFileValidatorSync = (options: {
  recommendedWidth: number;
  recommendedHeight: number;
  label: string;
}): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const file = control.value as File | null;
    if (!file) return null; // required is handled separately

    // Check file type
    const mime = file.type?.toLowerCase?.() ?? '';
    const allowedMimes = ['image/jpeg', 'image/jpg', 'image/png'];
    const isAllowedType = allowedMimes.includes(mime) || mime === 'image/jfif';

    // Also fall back to extension if mime is missing
    const name = (file.name || '').toLowerCase();
    const hasAllowedExt = name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png');

    if (!isAllowedType && !hasAllowedExt) {
      return { thumbnailType: true };
    }

    // Check file size
    if (file.size > MB_2) {
      return { thumbnailSize: true };
    }

    // For corrupted files, we'll do async validation in the component
    // Return null here and let the component handle async validation
    return null;
  };
};

